import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import PortalLayout from "@/layouts/PortalLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import CompanyLogo from "@/components/CompanyLogo";
import { useLang } from "@/context/LangContext";

export default function ExhibitorMatchmaking() {
  const { t } = useLang();
  const { exhibitorId } = useParams();
  const [ex, setEx] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [slots, setSlots] = useState([]);

  useEffect(() => {
    api.get(`/exhibitors/${exhibitorId}`).then((r) => {
      setEx(r.data.exhibitor);
      setContacts(r.data.contacts);
      if (r.data.contacts[0]) {
        setSelected(r.data.contacts[0].id);
      }
    }).catch(() => {});
  }, [exhibitorId]);

  useEffect(() => {
    if (selected) api.get(`/contacts/${selected}/slots`).then((r) => setSlots(r.data)).catch(() => {});
  }, [selected]);

  const book = async (slotId) => {
    try {
      await api.post("/meetings/book", { slot_id: slotId, purpose: "Découverte" });
      toast.success("Rendez-vous confirmé !");
      const r = await api.get(`/contacts/${selected}/slots`);
      setSlots(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  if (!ex) return <PortalLayout sidebar="member"><div className="py-20 text-center">{t("portal_exhibitormatchmaking_90")}</div></PortalLayout>;

  const groupedByDay = slots.reduce((acc, s) => {
    const day = new Date(s.date).toLocaleDateString("fr", { weekday: "long", day: "numeric", month: "long" });
    (acc[day] = acc[day] || []).push(s);
    return acc;
  }, {});

  return (
    <PortalLayout sidebar="member">
      <div className="flex items-center gap-4">
        <CompanyLogo src={ex.logo} name={ex.company_name} size={64} className="rounded-2xl" />
        <div>
          <h1 className="text-3xl font-bold text-navy">{ex.company_name}</h1>
          <p className="text-sm text-neutral-500">Stand {ex.booth}</p>
        </div>
      </div>
      <p className="mt-4 max-w-2xl text-neutral-700">{ex.description}</p>

      <div className="mt-8 grid gap-6 lg:grid-cols-[1fr_2fr]">
        <div className="rounded-2xl border border-neutral-100 bg-white p-5">
          <h2 className="font-serif text-lg text-neutral-900">Contacts disponibles</h2>
          <div className="mt-3 space-y-2">
            {contacts.map((c) => (
              <button key={c.id} onClick={() => setSelected(c.id)} className={`flex w-full items-center gap-3 rounded-xl p-3 text-left transition-all ${selected === c.id ? "bg-wine-50 ring-1 ring-wine-300" : "hover:bg-wine-50/40"}`} data-testid={`contact-${c.id}`}>
                <img src={c.photo} alt={c.name} className="h-10 w-10 rounded-full object-cover" />
                <div>
                  <div className="font-semibold text-neutral-900">{c.name}</div>
                  <div className="text-xs text-neutral-500">{c.role}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-neutral-100 bg-white p-5">
          <h2 className="font-serif text-lg text-neutral-900">{t("portal_exhibitormatchmaking_01")}</h2>
          {Object.entries(groupedByDay).map(([day, daySlots]) => (
            <div key={day} className="mt-5">
              <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">{day}</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {daySlots.map((s) => {
                  const time = new Date(s.date).toLocaleTimeString("fr", { hour: "2-digit", minute: "2-digit" });
                  const taken = s.status !== "available";
                  return (
                    <button key={s.id} disabled={taken} onClick={() => book(s.id)} className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${taken ? "bg-neutral-100 text-neutral-400 line-through" : "bg-wine-50 text-wine-700 hover:bg-wine-500 hover:text-white"}`} data-testid={`slot-${s.id}`}>
                      {time}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          {slots.length === 0 && <div className="mt-5 rounded-xl bg-wine-50/40 p-5 text-center text-sm text-neutral-500">{t("portal_exhibitormatchmaking_02")}</div>}
        </div>
      </div>
    </PortalLayout>
  );
}
